import React, { useMemo, useState } from 'react';
import { Avatar, Button, Card, Col, Divider, Empty, Input, InputNumber, List, Modal, Row, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CarOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  FileSearchOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { pdf } from '@react-pdf/renderer';
import DOMPurify from 'dompurify';
import FollowUpEditorModal from '../components/FollowUpEditorModal';
import SoftTag from '../components/SoftTag';
import InfoPill from '../components/InfoPill';
import HeaderField from '../components/HeaderField';
import StatusSelect from '../components/StatusSelect';
import { OFFER_STATUSES, ORDER_STATUSES, PRIORITY_TONES, TYPE_TONES, getOfferStatusTone, getOrderStatusTone } from '../constants/statuses';
import OfferPDFDocument from '../components/pdf/OfferPDFDocument';
import { formatDate, formatDateTime } from '../utils/formatters';
import { getOfferPdfFilename } from '../utils/pdfHelpers';
import { brandSemantic } from '../config/brand';
import { canEditOfferItem, computeOfferItemTotal, computeOfferItemInternalCost } from '../utils/quoteHelpers';
import { allItemsAccepted } from '../utils/orderHelpers';

const FULFILMENT_STATUSES = ['Production', 'Branding', 'Delivery', 'Accepted by Customer'];
const FULFILMENT_STATUS_TONES = {
  Production: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Branding: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
  Delivery: { color: brandSemantic.primary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  'Accepted by Customer': { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

const { Text, Title } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;

export default function OrderDetailsPage({
  order,
  lead,
  suppliers,
  supplierDirectory = [],
  quotes = [],
  offers = [],
  followUps,
  files = [],
  certificates = [],
  setFollowUps,
  currentUser,
  users,
  getFollowUpComments,
  handleAddFollowUpComment,
  getOfferStatus,
  updateOfferStatus,
  updateOfferPriority,
  showModal,
  onBack,
  onConvertToOrder,
  onOpenSourceOffer,
  onOpenSourceQuote,
  onOpenConvertedOrder,
  onDownloadFile,
  onUpdateOrderItemStatus,
  onMarkOrderAccepted,
  onUpdateOfferItem,
  t = (key) => key,
  canDelete = true,
}) {
  const [isFollowUpEditorOpen, setIsFollowUpEditorOpen] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [viewingFollowUpComments, setViewingFollowUpComments] = useState(null);
  const [newFollowUpComment, setNewFollowUpComment] = useState('');
  const [supplierStatusOverrides, setSupplierStatusOverrides] = useState({});
  const [supplierFieldOverrides, setSupplierFieldOverrides] = useState({});
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('products');
  const [isPricingEditMode, setIsPricingEditMode] = useState(false);

  // New-pipeline Offers/Orders carry a real Offer_Item/Order_Item array;
  // legacy records carry `items` as a plain count, so this also tells the
  // two rendering paths apart without touching the legacy one below.
  const isNewPipeline = Array.isArray(order.items);
  const isOffer = order.type === 'Offer';

  // Numbers tied to products may only be edited on an Offer that hasn't
  // been converted to an Order yet — once converted (or for an Order
  // itself), the figures are locked in.
  const canEditAmounts = order.type === 'Offer' && !order.convertedOrderId;
  const getItemOverrideKey = (section, item) => `${order.id}-${section.number}-${item.row}`;
  const supplierSections = (suppliers || []).map((section) => ({
    ...section,
    items: (section.items || []).map((item) => ({
      ...item,
      ...(supplierFieldOverrides[getItemOverrideKey(section, item)] || {}),
    })),
  }));
  const updateItemField = (section, item, field, value) => {
    const key = getItemOverrideKey(section, item);
    setSupplierFieldOverrides((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };
  const legacyOfferProducts = supplierSections.flatMap((section) => (
    section.items.map((item) => ({
      ...item,
      key: `${section.number}-${item.row}`,
      section,
    }))
  ));
  const legacyOfferCustomerTotal = legacyOfferProducts.reduce((sum, item) => {
    const cost = (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0)) + Number(item.deliveryCost || 0);
    return sum + cost * (1 + Number(item.margin || 0) / 100) * (1 - Number(item.discount || 0) / 100);
  }, 0);
  const legacyOfferInternalTotal = legacyOfferProducts.reduce((sum, item) => (
    sum + (Number(item.qty || 0) * Number(item.internalPrice ?? item.costFromSupplier ?? 0)) + Number(item.deliveryCost || 0)
  ), 0);
  const displayStatus = getOfferStatus(order.id, order.status);
  const totalValue = isNewPipeline
    ? order.items.reduce((sum, item) => sum + computeOfferItemTotal(item), 0)
    : supplierSections.reduce(
      (sum, section) => sum + section.items.reduce((sectionSum, item) => {
        const total = item.total ?? (
          Number(item.customerPrice ?? item.unitPrice ?? 0)
          * Number(item.qty || 0)
          * (1 - (Number(item.discount) || 0) / 100)
        );
        return sectionSum + Number(total || 0);
      }, 0),
      0,
    );
  const totalInternalValue = isNewPipeline
    ? order.items.reduce((sum, item) => sum + computeOfferItemInternalCost(item), 0)
    : supplierSections.reduce(
      (sum, section) => sum + section.items.reduce((sectionSum, item) => {
        const total = item.internalLineTotal ?? (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0));
        return sectionSum + Number(total || 0);
      }, 0),
      0,
    );
  const totalQty = isNewPipeline
    ? order.items.reduce((sum, item) => sum + Number(item.quantity ?? item.qty ?? 0), 0)
    : supplierSections.reduce(
      (sum, section) => sum + section.items.reduce((sectionSum, item) => sectionSum + item.qty, 0),
      0,
    );
  const createdBy = order.createdBy || currentUser?.name || 'Mads Mark-Buur';

  // The Offer/Order's source Quote — and through it, the originating Inquiry
  // — carries the header info (delivery address, deadline, requested
  // delivery date) that should also surface here. An Order doesn't link to a
  // Quote directly, so fall back to its source Offer's link.
  const directSourceQuoteIds = order.sourceQuoteIds?.length > 0
    ? order.sourceQuoteIds
    : (order.sourceQuoteId ? [order.sourceQuoteId] : []);
  const sourceOffer = order.sourceOfferId ? offers.find((item) => item.id === order.sourceOfferId) : null;
  const offerSourceQuoteIds = sourceOffer
    ? (sourceOffer.sourceQuoteIds?.length > 0 ? sourceOffer.sourceQuoteIds : (sourceOffer.sourceQuoteId ? [sourceOffer.sourceQuoteId] : []))
    : [];
  const sourceQuote = quotes.find((quote) => directSourceQuoteIds.includes(quote.id))
    || quotes.find((quote) => offerSourceQuoteIds.includes(quote.id))
    || null;
  const effectiveDeliveryDate = sourceQuote?.requestedDeliveryDate || order.deliveryDate;
  const relatedSourceQuoteIds = Array.from(new Set([...directSourceQuoteIds, ...offerSourceQuoteIds]));
  const relatedSourceQuotes = quotes.filter((quote) => (
    relatedSourceQuoteIds.includes(quote.id) || quote.convertedOfferId === order.id
  ));

  const followUpStats = useMemo(() => {
    const done = followUps.filter((item) => item.status === 'Done').length;
    const open = followUps.filter((item) => item.status !== 'Done').length;
    return { total: followUps.length, done, open };
  }, [followUps]);

  const supplierStatusTone = {
    Completed: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
    Production: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
    'Proof Pending': { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
    Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  };
  const getSupplierStatusSelectStyle = (status) => ({
    width: Math.min(Math.max(status.length * 7 + 34, 108), 152),
    '--status-color': (supplierStatusTone[status] || supplierStatusTone.Pending).color,
    '--status-bg': (supplierStatusTone[status] || supplierStatusTone.Pending).background,
    '--status-border': (supplierStatusTone[status] || supplierStatusTone.Pending).border,
  });
  const followUpStatusTone = {
    Done: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
    'In Production': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
    Open: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
    Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
    'Not Done': { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  };
  const followUpPriorityTone = {
    High: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
    Medium: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
    Low: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
  };
  const getFollowUpStatusSelectStyle = (status) => ({
    width: Math.min(Math.max(status.length * 7 + 34, 102), 148),
    '--status-color': (followUpStatusTone[status] || followUpStatusTone.Pending).color,
    '--status-bg': (followUpStatusTone[status] || followUpStatusTone.Pending).background,
    '--status-border': (followUpStatusTone[status] || followUpStatusTone.Pending).border,
  });
  const getFollowUpPrioritySelectStyle = (priority = 'Medium') => ({
    width: 112,
    '--status-color': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).color,
    '--status-bg': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).background,
    '--status-border': (followUpPriorityTone[priority] || followUpPriorityTone.Medium).border,
  });
  const getProductFiles = (item) => files.filter((file) => (
    file.itemId && (file.itemId === item.itemId || file.itemId === item.id)
  ));
  const getFileExtension = (file = {}) => {
    const fileName = file.fileName || file.name || '';
    return (file.fileType || fileName.split('.').pop() || '').toLowerCase();
  };
  const isPreviewableFile = (file) => ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'].includes(getFileExtension(file));
  const getPreviewUrl = (file) => {
    if (file.objectUrl || file.url || file.fileUrl) return file.objectUrl || file.url || file.fileUrl;
    if (getFileExtension(file) === 'pdf') return '/mock-files/certificate-preview.pdf';
    return `https://picsum.photos/seed/${encodeURIComponent(file.id || file.fileName || file.name)}/1200/800`;
  };
  const getItemProductName = (item) => item.productName || String(item.description || '').split(' — ')[0];
  const getQuoteForItem = (item) => {
    const productName = getItemProductName(item);
    const itemQuoteId = item.sourceQuoteId || item.quoteId;
    return quotes.find((quote) => quote.id === itemQuoteId)
      || relatedSourceQuotes.find((quote) => quote.inquiryItemId === item.itemId || quote.productName === productName)
      || quotes.find((quote) => quote.clientId === order.clientId && quote.productName === productName);
  };
  const getSelectedSupplierQuote = (quote, item, section) => {
    if (!quote) return null;
    const supplierId = item.supplierId
      ?? quote.suppliers?.find((supplier) => supplier.supplierName === (section?.name || section?.displayName))?.supplierId;
    return quote.supplierQuotes?.[supplierId]
      || Object.values(quote.supplierQuotes || {}).find((supplierQuote) => supplierQuote.selected)
      || Object.values(quote.supplierQuotes || {})[0]
      || null;
  };
  const toFileRecord = (file, quote, item) => ({
    ...file,
    id: file.id || `${quote?.id || order.id}-${item?.itemId || item?.id || 'product'}-${file.uid || file.name || file.fileName}`,
    itemId: file.itemId || item?.itemId || item?.id || quote?.inquiryItemId,
    productName: file.productName || getItemProductName(item || quote || {}),
    fileName: file.fileName || file.name,
    uploadDate: file.uploadDate || quote?.createdAt || order.date,
    uploadedBy: file.uploadedBy || quote?.createdBy || createdBy,
    description: file.description || `${getItemProductName(item || quote || {})} attachment`,
  });
  const sourceQuoteFiles = relatedSourceQuotes.flatMap((quote) => [
    ...(quote.files || []).map((file) => toFileRecord(file, quote, quote)),
    ...Object.values(quote.supplierQuotes || {}).flatMap((supplierQuote) => (
      (supplierQuote.files || []).map((file) => toFileRecord(file, quote, quote))
    )),
  ]);
  const displayFiles = [...files, ...sourceQuoteFiles].filter((file, index, allFiles) => (
    allFiles.findIndex((candidate) => `${candidate.itemId || ''}-${candidate.fileName}` === `${file.itemId || ''}-${file.fileName}`) === index
  ));
  const openProductDetails = (item, section = null) => {
    const quote = getQuoteForItem(item);
    const supplierQuote = getSelectedSupplierQuote(quote, item, section);
    const supplierId = supplierQuote?.supplierId
      ?? item.supplierId
      ?? quote?.suppliers?.find((supplier) => supplier.supplierName === (section?.name || section?.displayName))?.supplierId;
    const supplierName = quote?.suppliers?.find((supplier) => supplier.supplierId === supplierId)?.supplierName
      || section?.displayName
      || section?.name
      || 'Supplier';
    const complianceCertificates = certificates.filter((certificate) => (
      certificate.supplierId === supplierId
      && (certificate.productId == null || certificate.productId === quote?.productId)
    ));
    const productFiles = [
      ...getProductFiles(item),
      ...(quote?.files || []).map((file) => toFileRecord(file, quote, item)),
      ...(supplierQuote?.files || []).map((file) => toFileRecord(file, quote, item)),
    ].filter((file, index, allFiles) => (
      allFiles.findIndex((candidate) => (candidate.id || candidate.uid || candidate.fileName || candidate.name) === (file.id || file.uid || file.fileName || file.name)) === index
    ));
    const productComments = [
      ...(supplierQuote?.comments ? [{ id: 'supplier-comment', author: supplierName, comment: supplierQuote.comments }] : []),
      ...(!supplierQuote && (item.comments || item.notes) ? [{ id: 'item-comment', author: 'Internal', comment: item.comments || item.notes }] : []),
    ];
    setSelectedProductDetails({ item, quote, supplierQuote, complianceCertificates, productFiles, productComments });
  };
  const renderRichText = (value) => (value ? (
    <div
      className="rich-text-editor-content"
      style={{ fontSize: 14 }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
    />
  ) : <Text type="secondary">—</Text>);
  const pdfSupplierSections = supplierSections.map((section) => ({
    ...section,
    items: (section.items || []).map((item) => ({
      ...item,
      files: getProductFiles(item),
    })),
  }));

  const renderPDF = async () => {
    try {
      const blob = await pdf(
        <OfferPDFDocument
          order={order}
          supplierSections={pdfSupplierSections}
          lead={lead}
          variant="customer"
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getOfferPdfFilename(order.id, 'customer');
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(`Could not generate PDF: ${error.message}`);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Button
          icon={<HomeOutlined />}
          onClick={onBack}
        >
          Back to List
        </Button>
        <Space>
          {order.type === 'Offer' && displayStatus === 'Order Created' && !order.convertedOrderId && (
            <Button type="primary" icon={<SwapOutlined />} onClick={() => onConvertToOrder?.(order)}>
              Create Order
            </Button>
          )}
          <Button icon={<FilePdfOutlined />} onClick={() => renderPDF()}>
            Create Customer Quotation
          </Button>
          {canDelete && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirm({ title: 'Delete order', content: `Delete ${order.id}?`, onOk() { message.success('Order deleted'); onBack(); } })}
            >
              Delete
            </Button>
          )}
        </Space>
      </div>

      {isOffer ? (
        <Card style={{ borderRadius: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <Space wrap size={12} align="center">
              <Title level={3} style={{ margin: 0 }}>{order.id}</Title>
              {relatedSourceQuoteIds.length > 0 && (
                <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                  Source Internal Quote(s):{' '}
                  <Space size={4} wrap>
                    {relatedSourceQuoteIds.map((quoteId) => (
                      <Button
                        key={quoteId}
                        type="link"
                        size="small"
                        style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                        onClick={() => onOpenSourceQuote?.(quoteId)}
                      >
                        {quoteId}
                      </Button>
                    ))}
                  </Space>
                </Text>
              )}
              {order.sourceOfferId && (
                <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                  Source Offer:{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                    onClick={() => onOpenSourceOffer?.(order.sourceOfferId)}
                  >
                    {order.sourceOfferId}
                  </Button>
                </Text>
              )}
              {order.convertedOrderId && (
                <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                  Converted to Order:{' '}
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                    onClick={() => onOpenConvertedOrder?.(order.convertedOrderId)}
                  >
                    {order.convertedOrderId}
                  </Button>
                </Text>
              )}
            </Space>
            <Space size={8}>
              <SoftTag tone={TYPE_TONES.Offer}>Sales Quote</SoftTag>
              <StatusSelect
                value={displayStatus}
                onChange={(value) => updateOfferStatus(order.id, value)}
                statuses={OFFER_STATUSES}
                getTone={getOfferStatusTone}
                width={Math.min(Math.max(displayStatus.length * 7 + 34, 120), 260)}
              />
            </Space>
          </div>

          <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <InfoPill icon={<UserOutlined />} label="Client" value={order.client} />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <InfoPill icon={<PhoneOutlined />} label="Phone" value={lead?.phone || '—'} />
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <InfoPill icon={<MailOutlined />} label="Email" value={lead?.email || '—'} />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4}>
              <InfoPill
                icon={<CalendarOutlined />}
                label="Deadline"
                value={sourceQuote?.neededBefore ? formatDate(sourceQuote.neededBefore) : '—'}
                accent={brandSemantic.error}
              />
            </Col>
            <Col xs={12} sm={6} md={6} lg={4}>
              <InfoPill
                icon={<CarOutlined />}
                label="Delivery Date"
                value={effectiveDeliveryDate ? formatDate(effectiveDeliveryDate) : '—'}
              />
            </Col>
          </Row>
        </Card>
      ) : null}

      {isOffer && (
        <Card title="Sales Quote Details" style={{ borderRadius: 16 }}>
          <Row gutter={[24, 16]}>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Created Date">{formatDate(order.date)}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Created By">{createdBy}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Responsible">{order.responsible || createdBy}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Delivery Address">{sourceQuote?.deliveryAddress || '—'}</HeaderField>
            </Col>
          </Row>
        </Card>
      )}

      {!isOffer && (
      <Card style={{ borderRadius: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <Space wrap size={12} align="center">
            <Title level={3} style={{ margin: 0 }}>{order.id}</Title>
            {relatedSourceQuoteIds.length > 0 && (
              <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                Source Internal Quote(s):{' '}
                <Space size={4} wrap>
                  {relatedSourceQuoteIds.map((quoteId) => (
                    <Button
                      key={quoteId}
                      type="link"
                      size="small"
                      style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                      onClick={() => onOpenSourceQuote?.(quoteId)}
                    >
                      {quoteId}
                    </Button>
                  ))}
                </Space>
              </Text>
            )}
            {order.sourceOfferId && (
              <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                Source Sales Quote:{' '}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                  onClick={() => onOpenSourceOffer?.(order.sourceOfferId)}
                >
                  {order.sourceOfferId}
                </Button>
              </Text>
            )}
            {order.convertedOrderId && (
              <Text style={{ fontSize: 13, color: brandSemantic.textSecondary }}>
                Converted to Order:{' '}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', fontWeight: 700 }}
                  onClick={() => onOpenConvertedOrder?.(order.convertedOrderId)}
                >
                  {order.convertedOrderId}
                </Button>
              </Text>
            )}
          </Space>
          <Space size={8} wrap>
            <SoftTag tone={TYPE_TONES.Order}>Order</SoftTag>
            <StatusSelect
              value={displayStatus}
              onChange={(value) => updateOfferStatus(order.id, value)}
              statuses={ORDER_STATUSES}
              getTone={getOrderStatusTone}
              width={Math.min(Math.max(displayStatus.length * 7 + 34, 120), 260)}
            />
            <StatusSelect
              value={order.priority || 'Medium'}
              onChange={(value) => updateOfferPriority?.(order.id, value)}
              statuses={['Low', 'Medium', 'High']}
              getTone={(priority) => PRIORITY_TONES[priority] || PRIORITY_TONES.Medium}
              width={125}
            />
          </Space>
        </div>

        <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <InfoPill icon={<UserOutlined />} label="Client" value={order.client} />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <InfoPill icon={<PhoneOutlined />} label="Phone" value={lead?.phone || '—'} />
          </Col>
          <Col xs={24} sm={24} md={8} lg={8}>
            <InfoPill icon={<MailOutlined />} label="Email" value={lead?.email || '—'} />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <InfoPill
              icon={<CalendarOutlined />}
              label="Deadline"
              value={sourceQuote?.neededBefore ? formatDate(sourceQuote.neededBefore) : '—'}
              accent={brandSemantic.error}
            />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4}>
            <InfoPill
              icon={<CarOutlined />}
              label="Delivery Date"
              value={effectiveDeliveryDate ? formatDate(effectiveDeliveryDate) : '—'}
            />
          </Col>
        </Row>
      </Card>
      )}

      {!isOffer && (
        <Card title="Order Details" style={{ borderRadius: 16 }}>
          <Row gutter={[24, 16]}>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Created Date">{formatDate(order.date)}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Created By">{createdBy}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Responsible">{order.responsible || createdBy}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Delivery Address">{sourceQuote?.deliveryAddress || '—'}</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Items">{totalQty} pcs</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Suppliers">
                {isNewPipeline
                  ? new Set(order.items.map((item) => item.supplierName).filter(Boolean)).size
                  : supplierSections.length} suppliers
              </HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Internal Total">{totalInternalValue.toLocaleString('da-DK')} DKK</HeaderField>
            </Col>
            <Col xs={12} sm={8} md={6}>
              <HeaderField label="Customer Total">{totalValue.toLocaleString('da-DK')} DKK</HeaderField>
            </Col>
            {isNewPipeline && (
              <Col xs={24} sm={24} md={12}>
                <HeaderField label="Overall Status">
                  <Space size={8} wrap>
                    <SoftTag
                      tone={order.overallStatus === 'Accepted by Customer'
                        ? { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder }
                        : { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder }}
                      style={{ fontSize: 13, padding: '2px 10px' }}
                    >
                      {order.overallStatus}
                    </SoftTag>
                    {order.overallStatus !== 'Accepted by Customer' && (
                      <Button size="small" disabled={!allItemsAccepted(order)} onClick={onMarkOrderAccepted}>
                        Mark as Accepted by Customer
                      </Button>
                    )}
                  </Space>
                </HeaderField>
              </Col>
            )}
          </Row>
        </Card>
      )}

      <Tabs
        activeKey={activeDetailTab}
        onChange={setActiveDetailTab}
        items={[
          { key: 'products', label: isOffer ? 'Products in Quote' : 'Products in Order' },
          { key: 'files', label: 'Files' },
          { key: 'followUps', label: 'Follow-ups' },
        ]}
      />

      {activeDetailTab === 'products' && isNewPipeline && (
        <Card
          title={<Space><FileSearchOutlined style={{ color: '#4B78A8' }} /><span style={{ fontSize: 18, fontWeight: 700 }}>{isOffer ? 'Products in Quote' : 'Products & Suppliers'}</span></Space>}
          extra={order.type === 'Offer' && canEditAmounts ? (
            <Button icon={<EditOutlined />} onClick={() => setIsPricingEditMode((value) => !value)}>
              {isPricingEditMode ? 'Done' : 'Edit'}
            </Button>
          ) : null}
          style={{ borderRadius: 16 }}
        >
          <Table
            rowKey="id"
            dataSource={order.items}
            pagination={false}
            size="middle"
            columns={[
              {
                title: 'Item',
                dataIndex: 'productName',
                key: 'productName',
                render: (value, item) => isOffer ? <strong>{value}</strong> : (
                  <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 600, textAlign: 'left' }} onClick={() => openProductDetails(item)}>
                    {value}
                  </Button>
                ),
              },
              { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 70, align: 'right', render: (value) => Number(value || 0).toLocaleString('da-DK') },
              {
                title: 'Unit Price',
                dataIndex: 'unitPrice',
                key: 'unitPrice',
                width: 110,
                align: 'right',
                render: (value, item) => Number(value ?? item.confirmedUnitPrice ?? 0).toLocaleString('da-DK'),
              },
              ...(isOffer ? [{
                title: 'Shipping Cost',
                dataIndex: 'shippingCost',
                key: 'shippingCost',
                width: 115,
                align: 'right',
                render: (value) => Number(value || 0).toLocaleString('da-DK'),
              }] : []),
              ...(order.type === 'Offer' ? [
                {
                  title: 'Margin %',
                  dataIndex: 'marginPercent',
                  key: 'marginPercent',
                  width: 90,
                  align: 'right',
                  render: (value, item) => (isPricingEditMode && canEditOfferItem(order) ? (
                    <InputNumber size="small" min={0} value={value ?? 0} style={{ width: '100%' }} onChange={(val) => onUpdateOfferItem?.(item.id, 'marginPercent', val ?? 0)} />
                  ) : `${Number(value || 0).toLocaleString('da-DK')}%`),
                },
                {
                  title: 'Discount %',
                  dataIndex: 'discountPercent',
                  key: 'discountPercent',
                  width: 95,
                  align: 'right',
                  render: (value, item) => (isPricingEditMode && canEditOfferItem(order) ? (
                    <InputNumber size="small" min={0} max={100} value={value ?? 0} style={{ width: '100%' }} onChange={(val) => onUpdateOfferItem?.(item.id, 'discountPercent', val ?? 0)} />
                  ) : `${Number(value || 0).toLocaleString('da-DK')}%`),
                },
                {
                  title: 'Internal Price',
                  key: 'internalCost',
                  width: 110,
                  align: 'right',
                  render: (_, item) => Number(computeOfferItemInternalCost(item)).toLocaleString('da-DK'),
                },
                {
                  title: 'Customer Total',
                  key: 'customerTotal',
                  width: 125,
                  align: 'right',
                  render: (_, item) => <strong>{Number(computeOfferItemTotal(item)).toLocaleString('da-DK')}</strong>,
                },
              ] : [
                {
                  title: 'Fulfilment Status',
                  dataIndex: 'fulfilmentStatus',
                  key: 'fulfilmentStatus',
                  width: 180,
                  render: (value, item) => (
                    <Select
                      value={value}
                      onChange={(next) => onUpdateOrderItemStatus?.(item.id, next)}
                      size="small"
                      variant="borderless"
                      className="status-tag-select"
                      style={{
                        width: 160,
                        '--status-color': (FULFILMENT_STATUS_TONES[value] || FULFILMENT_STATUS_TONES.Production).color,
                        '--status-bg': (FULFILMENT_STATUS_TONES[value] || FULFILMENT_STATUS_TONES.Production).background,
                        '--status-border': (FULFILMENT_STATUS_TONES[value] || FULFILMENT_STATUS_TONES.Production).border,
                      }}
                      dropdownMatchSelectWidth={false}
                      options={FULFILMENT_STATUSES.map((status) => ({ value: status, label: status }))}
                    />
                  ),
                },
                {
                  title: 'Confirmed Total',
                  key: 'confirmedTotal',
                  width: 125,
                  align: 'right',
                  render: (_, item) => <strong>{Number(computeOfferItemInternalCost(item)).toLocaleString('da-DK')}</strong>,
                },
              ]),
              { title: 'Country', dataIndex: 'countryOfProduction', key: 'countryOfProduction', width: 110, render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span> },
            ]}
          />
          {isOffer && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 28, marginTop: 16, padding: '12px 16px', background: brandSemantic.layout, borderRadius: 10 }}>
              <Text>Internal Grand Total: <strong>{totalInternalValue.toLocaleString('da-DK')} DKK</strong></Text>
              <Text>Customer Grand Total: <strong>{totalValue.toLocaleString('da-DK')} DKK</strong></Text>
            </div>
          )}
        </Card>
      )}

      {activeDetailTab === 'products' && !isNewPipeline && (
      <Card
        title={<Space><FileSearchOutlined style={{ color: '#4B78A8' }} /><span style={{ fontSize: 18, fontWeight: 700 }}>{isOffer ? 'Products in Quote' : 'Products & Suppliers'}</span></Space>}
        extra={order.type === 'Offer' && canEditAmounts ? (
          <Button icon={<EditOutlined />} onClick={() => setIsPricingEditMode((value) => !value)}>
            {isPricingEditMode ? 'Done' : 'Edit'}
          </Button>
        ) : null}
        style={{ borderRadius: 16 }}
      >
        {order.type === 'Offer' ? (
          <>
            <Table
              rowKey="key"
              dataSource={legacyOfferProducts}
              pagination={false}
              size="middle"
              scroll={{ x: 1050 }}
              columns={[
                {
                  title: 'Item',
                  dataIndex: 'description',
                  key: 'description',
                  render: (value) => <strong>{value}</strong>,
                },
                { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 70, align: 'right' },
                {
                  title: 'Unit Price', key: 'unitPrice', width: 110, align: 'right',
                  render: (_, item) => Number(item.internalPrice ?? item.costFromSupplier ?? 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Shipping Cost', dataIndex: 'deliveryCost', key: 'deliveryCost', width: 120, align: 'right',
                  render: (value) => Number(value || 0).toLocaleString('da-DK'),
                },
                {
                  title: 'Internal Price', key: 'internalPrice', width: 125, align: 'right',
                  render: (_, item) => {
                    const value = (Number(item.qty || 0) * Number(item.internalPrice ?? item.costFromSupplier ?? 0)) + Number(item.deliveryCost || 0);
                    return <strong>{value.toLocaleString('da-DK')}</strong>;
                  },
                },
                {
                  title: 'Margin %', dataIndex: 'margin', key: 'margin', width: 100, align: 'right',
                  render: (value, item) => isPricingEditMode ? (
                    <InputNumber size="small" min={0} value={value ?? 0} style={{ width: '100%' }} onChange={(next) => updateItemField(item.section, item, 'margin', next ?? 0)} />
                  ) : `${Number(value || 0).toLocaleString('da-DK')}%`,
                },
                {
                  title: 'Discount %', dataIndex: 'discount', key: 'discount', width: 105, align: 'right',
                  render: (value, item) => isPricingEditMode ? (
                    <InputNumber size="small" min={0} max={100} value={value ?? 0} style={{ width: '100%' }} onChange={(next) => updateItemField(item.section, item, 'discount', next ?? 0)} />
                  ) : `${Number(value || 0).toLocaleString('da-DK')}%`,
                },
                {
                  title: 'Customer Total', key: 'customerTotal', width: 140, align: 'right',
                  render: (_, item) => {
                    const cost = (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0)) + Number(item.deliveryCost || 0);
                    const price = cost * (1 + Number(item.margin || 0) / 100) * (1 - Number(item.discount || 0) / 100);
                    return <strong>{price.toLocaleString('da-DK')}</strong>;
                  },
                },
              ]}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 28, marginTop: 16, padding: '12px 16px', background: brandSemantic.layout, borderRadius: 10 }}>
              <Text>Internal Grand Total: <strong>{legacyOfferInternalTotal.toLocaleString('da-DK')} DKK</strong></Text>
              <Text>Customer Grand Total: <strong>{legacyOfferCustomerTotal.toLocaleString('da-DK')} DKK</strong></Text>
            </div>
          </>
        ) : (
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
          {supplierSections.map((section, index) => {
            const sectionCustomerTotal = section.items.reduce((sum, item) => {
              const total = item.total ?? (
                Number(item.customerPrice ?? item.unitPrice ?? 0)
                * Number(item.qty || 0)
                * (1 - (Number(item.discount) || 0) / 100)
              );
              return sum + Number(total || 0);
            }, 0);
            const sectionInternalTotal = section.items.reduce((sum, item) => {
              const total = item.internalLineTotal ?? (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0));
              return sum + Number(total || 0);
            }, 0);
            const supplierDisplayName = section.displayName || section.name;
            const getItemStatusKey = (item) => `${order.id}-${section.number}-${item.row}`;
            const getCurrentItemStatus = (item) => (
              supplierStatusOverrides[getItemStatusKey(item)] || item.status || section.status || 'Pending'
            );
            return (
              <div key={`${order.id}-${section.name}`} style={{ border: `1px solid ${brandSemantic.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: '#fff' }}>
                  <Space wrap size={10}>
                    <FileOutlined style={{ color: brandSemantic.primary, fontSize: 16 }} />
                    <Text style={{ fontSize: 15, fontWeight: 700, color: '#25324B' }}>{supplierDisplayName}</Text>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: 24,
                        padding: '0 8px',
                        borderRadius: 6,
                        border: '1px solid #91C3FF',
                        background: '#F2F8FF',
                        color: '#2563EB',
                        fontSize: 11,
                        fontWeight: 500,
                        lineHeight: '22px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Supplier #{section.number}
                    </span>
                  </Space>
                </div>
                <Table
                  className="supplier-split-table"
                  rowKey="row"
                  dataSource={section.items}
                  pagination={false}
                  bordered={false}
                  size="middle"
                  tableLayout="fixed"
                  scroll={{ x: 1700 }}
                  columns={[
                    { title: '#', dataIndex: 'row', key: 'row', width: 40 },
                    ...(order.type === 'Order' ? [{
                      title: 'Status',
                      key: 'status',
                      width: 150,
                      render: (_, item) => {
                        const currentStatus = getCurrentItemStatus(item);
                        return (
                          <Select
                            value={currentStatus}
                            onChange={(value) => {
                              setSupplierStatusOverrides((prev) => ({
                                ...prev,
                                [getItemStatusKey(item)]: value,
                              }));
                            }}
                            size="small"
                            variant="borderless"
                            className="status-tag-select"
                            style={getSupplierStatusSelectStyle(currentStatus)}
                            dropdownMatchSelectWidth={false}
                            options={[
                              { value: 'Pending', label: 'Pending' },
                              { value: 'Proof Pending', label: 'Proof Pending' },
                              { value: 'Production', label: 'Production' },
                              { value: 'Completed', label: 'Completed' },
                            ]}
                          />
                        );
                      },
                    }] : []),
                    {
                      title: 'Item',
                      dataIndex: 'description',
                      key: 'productName',
                      width: 260,
                      render: (value, item) => (
                        <Space direction="vertical" size={0}>
                          <Button type="link" style={{ padding: 0, height: 'auto', fontWeight: 600, textAlign: 'left', whiteSpace: 'normal' }} onClick={() => openProductDetails(item, section)}>
                            {value}
                          </Button>
                          {item.article && <Text type="secondary">Article: {item.article}</Text>}
                        </Space>
                      ),
                    },
                    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 70, align: 'right', render: (value) => value.toLocaleString('da-DK') },
                    {
                      title: 'Base price',
                      dataIndex: 'baseInternalPrice',
                      key: 'baseInternalPrice',
                      width: 90,
                      align: 'right',
                      render: (value, item) => (isPricingEditMode && canEditAmounts ? (
                        <InputNumber
                          size="small"
                          min={0}
                          value={value ?? 0}
                          onChange={(val) => updateItemField(section, item, 'baseInternalPrice', val ?? 0)}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        value == null ? <span style={{ color: '#bfbfbf' }}>—</span> : Number(value).toLocaleString('da-DK')
                      )),
                    },
                    {
                      title: 'Production',
                      dataIndex: 'productionCost',
                      key: 'productionCost',
                      width: 90,
                      align: 'right',
                      render: (value, item) => (isPricingEditMode && canEditAmounts ? (
                        <InputNumber
                          size="small"
                          min={0}
                          value={value ?? 0}
                          onChange={(val) => updateItemField(section, item, 'productionCost', val ?? 0)}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        value == null ? <span style={{ color: '#bfbfbf' }}>—</span> : Number(value).toLocaleString('da-DK')
                      )),
                    },
                    {
                      title: 'Delivery',
                      dataIndex: 'deliveryCost',
                      key: 'deliveryCost',
                      width: 85,
                      align: 'right',
                      render: (value, item) => (isPricingEditMode && canEditAmounts ? (
                        <InputNumber
                          size="small"
                          min={0}
                          value={value ?? 0}
                          onChange={(val) => updateItemField(section, item, 'deliveryCost', val ?? 0)}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        value == null ? <span style={{ color: '#bfbfbf' }}>—</span> : Number(value).toLocaleString('da-DK')
                      )),
                    },
                    {
                      title: 'Margin %',
                      dataIndex: 'margin',
                      key: 'margin',
                      width: 90,
                      align: 'right',
                      render: (value, item) => (isPricingEditMode && canEditAmounts ? (
                        <InputNumber
                          size="small"
                          min={0}
                          value={value ?? 0}
                          onChange={(val) => updateItemField(section, item, 'margin', val ?? 0)}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        value == null ? <span style={{ color: '#bfbfbf' }}>—</span> : `${Number(value).toLocaleString('da-DK')}%`
                      )),
                    },
                    {
                      title: 'Discount %',
                      dataIndex: 'discount',
                      key: 'discount',
                      width: 95,
                      align: 'right',
                      render: (value, item) => (isPricingEditMode && canEditAmounts ? (
                        <InputNumber
                          size="small"
                          min={0}
                          max={100}
                          value={value ?? 0}
                          onChange={(val) => updateItemField(section, item, 'discount', val ?? 0)}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        `${Number(value || 0).toLocaleString('da-DK')}%`
                      )),
                    },
                    {
                      title: 'Customer price',
                      key: 'customerPrice',
                      width: 125,
                      align: 'right',
                      render: (_, item) => {
                        const value = item.customerPrice ?? item.unitPrice ?? 0;
                        return isPricingEditMode && canEditAmounts ? (
                          <InputNumber
                            size="small"
                            min={0}
                            value={value}
                            onChange={(val) => updateItemField(section, item, item.customerPrice != null ? 'customerPrice' : 'unitPrice', val ?? 0)}
                            style={{ width: '100%' }}
                          />
                        ) : Number(value).toLocaleString('da-DK');
                      },
                    },
                    {
                      title: 'Total cost',
                      key: 'internalCost',
                      width: 90,
                      align: 'right',
                      render: (_, item) => Number(item.internalPrice ?? item.costFromSupplier ?? 0).toLocaleString('da-DK'),
                    },
                    {
                      title: 'Internal total',
                      key: 'internalLineTotal',
                      width: 125,
                      align: 'right',
                      render: (_, item) => {
                        const total = item.internalLineTotal ?? (Number(item.internalPrice ?? item.costFromSupplier ?? 0) * Number(item.qty || 0));
                        return <strong>{Number(total || 0).toLocaleString('da-DK')}</strong>;
                      },
                    },
                    {
                      title: 'Customer total',
                      key: 'customerLineTotal',
                      width: 125,
                      align: 'right',
                      render: (_, item) => {
                        const total = item.total ?? (
                          Number(item.customerPrice ?? item.unitPrice ?? 0)
                          * Number(item.qty || 0)
                          * (1 - (Number(item.discount) || 0) / 100)
                        );
                        return <strong>{Number(total || 0).toLocaleString('da-DK')}</strong>;
                      },
                    },
                  ]}
                />
                <div style={{ textAlign: 'right', padding: '8px 20px', borderTop: `1px solid ${brandSemantic.borderSecondary}`, color: brandSemantic.textSecondary, fontSize: 13 }}>
                  Subtotal — {supplierDisplayName.split(' ')[0]}:
                  <span style={{ marginLeft: 10 }}>Internal: <strong style={{ color: '#000', fontSize: 14 }}>{sectionInternalTotal.toLocaleString('da-DK')}</strong></span>
                  <span style={{ marginLeft: 14 }}>Customer: <strong style={{ color: '#000', fontSize: 14 }}>{sectionCustomerTotal.toLocaleString('da-DK')}</strong></span>
                </div>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
            <Text style={{ fontSize: 16, color: '#6B7A90' }}>
              Grand total:
              <span style={{ marginLeft: 12 }}>Internal <strong style={{ marginLeft: 6, fontSize: 18, color: '#1F2A44' }}>{totalInternalValue.toLocaleString('da-DK')} DKK</strong></span>
              <span style={{ marginLeft: 18 }}>Customer <strong style={{ marginLeft: 6, fontSize: 18, color: '#1F2A44' }}>{totalValue.toLocaleString('da-DK')} DKK</strong></span>
            </Text>
          </div>
        </Space>
        )}
      </Card>
      )}

      {activeDetailTab === 'files' && (
      <Card
        title={<Space><FileOutlined style={{ color: brandSemantic.primary }} /><span style={{ fontSize: 18, fontWeight: 700 }}>Files</span></Space>}
        style={{ borderRadius: 16 }}
      >
        {displayFiles.length === 0 ? (
          <Empty description="No files attached" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            rowKey="id"
            dataSource={displayFiles}
            pagination={false}
            size="middle"
            columns={[
              {
                title: 'File name',
                dataIndex: 'fileName',
                key: 'fileName',
                render: (value, record) => (
                  <Space>
                    <FileOutlined style={{ color: brandSemantic.primary }} />
                    <span>{value}</span>
                    {record.approvalDocument && (
                      <SoftTag
                        tone={{ color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder }}
                        style={{ fontSize: 11 }}
                      >
                        Approval
                      </SoftTag>
                    )}
                  </Space>
                ),
              },
              {
                title: 'Product',
                dataIndex: 'productName',
                key: 'productName',
                width: 160,
                render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
              },
              { title: 'Description', dataIndex: 'description', key: 'description' },
              { title: 'Uploaded by', dataIndex: 'uploadedBy', key: 'uploadedBy', width: 160 },
              { title: 'Upload date', dataIndex: 'uploadDate', key: 'uploadDate', width: 150, render: (value) => formatDateTime(value) },
              {
                title: 'Actions',
                key: 'action',
                width: 130,
                render: (_, record) => (
                  <Space direction="vertical" size={2}>
                    {isPreviewableFile(record) && (
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        style={{ padding: 0, height: 'auto' }}
                        onClick={() => setPreviewFile({
                          name: record.fileName,
                          url: getPreviewUrl(record),
                          isImage: getFileExtension(record) !== 'pdf',
                        })}
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => onDownloadFile?.(record)}
                    >
                      Download
                    </Button>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>
      )}

      <Modal
        title={previewFile?.name}
        open={Boolean(previewFile)}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width="80%"
        styles={{ body: { padding: 0, height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
        destroyOnClose
      >
        {previewFile?.isImage ? (
          <img src={previewFile.url} alt={previewFile.name} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
        ) : previewFile && (
          <iframe src={previewFile.url} title={previewFile.name} style={{ width: '100%', height: '100%', border: 'none' }} />
        )}
      </Modal>

      <Modal
        title={selectedProductDetails ? `Product Details — ${getItemProductName(selectedProductDetails.item)}` : 'Product Details'}
        open={Boolean(selectedProductDetails)}
        onCancel={() => setSelectedProductDetails(null)}
        footer={<Button onClick={() => setSelectedProductDetails(null)}>Close</Button>}
        width={760}
        destroyOnClose
      >
        {selectedProductDetails && (
          <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <div>
              <Text strong>Product Details</Text>
              <div style={{ marginTop: 6 }}>
                {renderRichText(selectedProductDetails.supplierQuote?.productDetails || selectedProductDetails.quote?.specifications || selectedProductDetails.item.productDetails || selectedProductDetails.item.description)}
              </div>
            </div>
            <div>
              <Text strong>Branding / Decoration</Text>
              <div style={{ marginTop: 6 }}>
                {renderRichText(selectedProductDetails.supplierQuote?.brandingDecoration || selectedProductDetails.quote?.brandingDescription || selectedProductDetails.item.brandingDecoration || selectedProductDetails.item.brandingDescription)}
              </div>
            </div>
            <div>
              <Text strong>Compliance Info</Text>
              <div style={{ marginTop: 6 }}>
                {selectedProductDetails.complianceCertificates.length > 0 ? (
                  <Space wrap size={4}>
                    {selectedProductDetails.complianceCertificates.map((certificate) => <Tag key={certificate.id}>{certificate.name}</Tag>)}
                  </Space>
                ) : <Text type="secondary">No certificates on file for this supplier</Text>}
              </div>
            </div>
            <div>
              <Text strong>Comments</Text>
              <div style={{ marginTop: 6 }}>
                {selectedProductDetails.productComments.length > 0 ? (
                  <List
                    size="small"
                    bordered
                    dataSource={selectedProductDetails.productComments}
                    renderItem={(comment) => (
                      <List.Item>
                        <Space direction="vertical" size={0}>
                          <Text strong>{comment.author || comment.userName || 'Internal'}</Text>
                          <span>{comment.comment}</span>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : <Text type="secondary">No comments</Text>}
              </div>
            </div>
            <div>
              <Text strong>Attachments</Text>
              <div style={{ marginTop: 6 }}>
                {selectedProductDetails.productFiles.length > 0 ? (
                  <Space direction="vertical" size={2}>
                    {selectedProductDetails.productFiles.map((file) => (
                      <Button key={file.id || file.uid || file.fileName || file.name} type="link" icon={<DownloadOutlined />} style={{ padding: 0, height: 'auto', justifyContent: 'flex-start' }} onClick={() => onDownloadFile?.(file)}>
                        {file.fileName || file.name}
                      </Button>
                    ))}
                  </Space>
                ) : <Text type="secondary">No attachments</Text>}
              </div>
            </div>
          </Space>
        )}
      </Modal>

      {activeDetailTab === 'followUps' && (
      <Card
        title={
          <Space>
            <BellOutlined style={{ color: brandSemantic.primary }} />
            <span style={{ fontSize: 18, fontWeight: 700 }}>Follow-ups</span>
            <Text style={{ color: '#6B7A90' }}>— {followUpStats.total} total · {followUpStats.open} open · {followUpStats.done} done</Text>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingFollowUp(null);
              setIsFollowUpEditorOpen(true);
            }}
          >
            Add follow-up
          </Button>
        }
        style={{ borderRadius: 16 }}
      >
        <Table
          rowKey="id"
          dataSource={followUps}
          pagination={false}
          bordered={false}
          size="middle"
          columns={[
            { title: 'Follow-up date', dataIndex: 'date', key: 'date', width: 140, render: (value) => <strong>{formatDate(value)}</strong> },
            { title: 'Description', dataIndex: 'action', key: 'action' },
            { title: 'Responsible', dataIndex: 'responsible', key: 'responsible', width: 160 },
            {
              title: 'Priority',
              dataIndex: 'priority',
              key: 'priority',
              width: 120,
              render: (value = 'Medium', record) => (
                <Select
                  value={value}
                  onChange={(nextValue) => {
                    setFollowUps((prev) => prev.map((item) => (
                      item.id === record.id ? { ...item, priority: nextValue } : item
                    )));
                  }}
                  size="small"
                  variant="borderless"
                  className="status-tag-select"
                  style={getFollowUpPrioritySelectStyle(value)}
                  dropdownMatchSelectWidth={false}
                  options={[
                    { value: 'High', label: 'High' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'Low', label: 'Low' },
                  ]}
                />
              ),
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 130,
              render: (value, record) => (
                <Select
                  value={value}
                  onChange={(nextValue) => {
                    setFollowUps((prev) => prev.map((item) => (
                      item.id === record.id ? { ...item, status: nextValue } : item
                    )));
                  }}
                  size="small"
                  variant="borderless"
                  className="status-tag-select"
                  style={getFollowUpStatusSelectStyle(value)}
                  dropdownMatchSelectWidth={false}
                  options={[
                    { value: 'Pending', label: 'Pending' },
                    { value: 'Open', label: 'Open' },
                    { value: 'In Production', label: 'In Production' },
                    { value: 'Done', label: 'Done' },
                    { value: 'Not Done', label: 'Not Done' },
                  ]}
                />
              ),
            },
            {
              title: 'Comments',
              key: 'comments',
              width: 100,
              render: (_, record) => {
                const commentItems = getFollowUpComments(record);
                if (commentItems.length === 0) {
                  return (
                    <Button
                      type="link"
                      size="small"
                      icon={<PlusOutlined />}
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => setViewingFollowUpComments(record)}
                    >
                      Add
                    </Button>
                  );
                }
                return (
                  <Button
                    type="link"
                    icon={<CommentOutlined />}
                    style={{ padding: 0 }}
                    onClick={() => setViewingFollowUpComments(record)}
                  >
                    {commentItems.length}
                  </Button>
                );
              },
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 100,
              render: (_, record) => (
                <Space direction="vertical" size={2}>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => {
                      setEditingFollowUp(record);
                      setIsFollowUpEditorOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  {canDelete && (
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => {
                        setFollowUps((prev) => prev.filter((item) => item.id !== record.id));
                        if (viewingFollowUpComments?.id === record.id) {
                          setViewingFollowUpComments(null);
                          setNewFollowUpComment('');
                        }
                        message.success('Follow-up deleted');
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />

        <FollowUpEditorModal
          open={isFollowUpEditorOpen}
          followUp={editingFollowUp}
          users={users}
          currentUser={currentUser}
          t={t}
          onCancel={() => {
            setIsFollowUpEditorOpen(false);
            setEditingFollowUp(null);
          }}
          onSave={(values) => {
            if (values.id) {
              setFollowUps((prev) => prev.map((item) => (
                item.id === values.id ? { ...item, ...values } : item
              )));
              if (viewingFollowUpComments?.id === values.id) {
                setViewingFollowUpComments((prev) => (prev ? { ...prev, ...values } : prev));
              }
              message.success('Follow-up updated');
            } else {
              setFollowUps((prev) => [
                ...prev,
                { ...values, id: Date.now(), offerId: order.id, reminder: '', comments: [] },
              ]);
              message.success('Follow-up added');
            }
            setIsFollowUpEditorOpen(false);
            setEditingFollowUp(null);
          }}
        />

        <Modal
          title="Comments"
          open={Boolean(viewingFollowUpComments)}
          onCancel={() => {
            setViewingFollowUpComments(null);
            setNewFollowUpComment('');
          }}
          footer={null}
          width={760}
          destroyOnClose
        >
          <div style={{ marginBottom: 16 }}>
            <TextArea
              rows={3}
              placeholder="Add a new comment..."
              value={newFollowUpComment}
              onChange={(e) => setNewFollowUpComment(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <Button
              type="primary"
              onClick={() => {
                const createdComment = handleAddFollowUpComment(viewingFollowUpComments?.id, newFollowUpComment);
                if (createdComment) {
                  setViewingFollowUpComments((prev) => ({
                    ...prev,
                    comments: [...getFollowUpComments(prev), createdComment],
                  }));
                }
                setNewFollowUpComment('');
              }}
            >
              Add Comment
            </Button>
          </div>
          <Divider />
          {viewingFollowUpComments && getFollowUpComments(viewingFollowUpComments).length === 0 ? (
            <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={viewingFollowUpComments ? [...getFollowUpComments(viewingFollowUpComments)].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : []}
              renderItem={(comment) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={(
                      <Space>
                        <strong>{comment.author}</strong>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(comment.timestamp)}
                        </Text>
                      </Space>
                    )}
                    description={comment.comment}
                  />
                </List.Item>
              )}
            />
          )}
        </Modal>
      </Card>
      )}
    </div>
  );
}
